import React, { useState, useEffect } from "react";

type IPreloader = {
  imgSrc: string;
  imgAlt: string;
  placeholderSrc?: string; // Optional placeholder image source
  imgClassName?: string;
  placeholderClassName?: string; // Optional class for placeholder styling
  loading?: "eager" | "lazy";
  imgWidth?: number;
  imgHeight?: number;
  imgMaxWidth?: number;
  imgMaxHeight?: number;
};

const PreloadImage: React.FC<IPreloader> = ({
  imgSrc,
  imgAlt,
  placeholderSrc, // Low-res or blurred version of the image
  imgClassName,
  placeholderClassName = "blur-sm transition-opacity duration-300",
  loading = "eager",
  imgWidth,
  imgHeight,
  imgMaxWidth,
  imgMaxHeight,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [src, setSrc] = useState(placeholderSrc || imgSrc);

  useEffect(() => {
    // Reset loading state when image source changes
    setIsLoaded(false);
    setSrc(placeholderSrc || imgSrc);

    const img = new Image();
    img.src = imgSrc;

    img.onload = () => {
      setSrc(imgSrc);
      setIsLoaded(true);
    };

    // If no placeholder is provided, we'll still show the main image
    // This will cause the natural browser loading behavior
    if (!placeholderSrc) {
      setSrc(imgSrc);
    }
  }, [imgSrc, placeholderSrc]);

  return (
    <div
      className="relative overflow-hidden"
      style={{ width: imgWidth, height: imgHeight }}
    >
      {/* Placeholder image with blur effect */}
      {placeholderSrc && !isLoaded && (
        <img
          src={placeholderSrc}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover ${placeholderClassName}`}
          width={imgWidth}
          height={imgHeight}
        />
      )}

      {/* Main image that will load */}
      <img
        src={src}
        alt={imgAlt}
        className={`${imgClassName} ${
          isLoaded ? "opacity-100" : "opacity-0"
        } transition-opacity duration-300`}
        loading={loading}
        width={imgWidth}
        height={imgHeight}
        style={{
          maxWidth: imgMaxWidth,
          maxHeight: imgMaxHeight,
        }}
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

export default PreloadImage;
